import { ReactNode } from 'react';
import { Card as BaseCard, styled } from '@mui/material';
import { Card as CardSchema } from '@/schemas/sceneDataSchema';
import { CardBorderColor } from '@/constants/cardTypes';
import { Theme } from '@mui/material/styles';
import { Colors } from '@/constants/colorTypes';
import { lighten } from '@mui/material/styles';
interface CardProps {
  children: ReactNode;
  component: CardSchema;
}

export function Card({ component, children }: CardProps) {
  return (
    <StyledCard
      id={component.id ?? undefined}
      border={component.border}
      borderRadius={component.border_radius}
      borderColor={component.border_color ?? undefined}
      backgroundColor={component.background_color ?? undefined}
      shadow={component.shadow}
    >
      {children}
    </StyledCard>
  );
}

interface StyledCardProps {
  border: boolean;
  borderRadius: string;
  borderColor?: CardBorderColor;
  shadow: string;
  backgroundColor?: Colors;
}

const cardBackgroundType = (theme: Theme, backgroundColor?: Colors): string => {
  switch (backgroundColor) {
    case Colors.INFO:
      return lighten(theme.palette.info.main, 0.9);
    case Colors.SUCCESS:
      return theme.palette.success.main;
    case Colors.WARNING:
      return theme.palette.warning.main;
    case Colors.ERROR:
      return theme.palette.error.main;
    default:
      return '';
  }
};

const StyledCard = styled(BaseCard, {
  shouldForwardProp: (prop) =>
    prop !== 'borderRadius' &&
    prop !== 'borderColor' &&
    prop !== 'shadow' &&
    prop !== 'border' &&
    prop !== 'backgroundColor',
})<StyledCardProps>`
  border: ${({ border, borderColor }) => {
    if (!border) return 'none';
    if (!borderColor || borderColor === CardBorderColor.BORDER_SECONDARY) {
      return '1px solid var(--mui-palette-border-secondary)';
    }
    if (borderColor === CardBorderColor.BORDER) {
      return '1px solid var(--mui-palette-border-main)';
    }
    return `1px solid var(--mui-palette-${borderColor}-main)`;
  }};
  border-radius: ${({ borderRadius }) => {
    switch (borderRadius) {
      case 'small':
        return 'var(--border-radius-small)';
      case 'medium':
        return 'var(--border-radius-medium)';
      case 'large':
        return 'var(--border-radius-large)';
      default:
        return 'var(--border-radius-large)';
    }
  }};
  box-shadow: ${({ shadow }) => {
    switch (shadow) {
      case 'none':
        return 'var(--shadow-none)';
      case 'small':
        return 'var(--shadow-small)';
      case 'large':
        return 'var(--shadow-large)';
      default:
        return 'var(--shadow-none)';
    }
  }};
  background-color: ${({ theme, backgroundColor }) => cardBackgroundType(theme, backgroundColor)};
`;
